import { useState, useMemo, useCallback } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { stateAbbreviations } from '@/components/USAMapNavigator';

const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

interface StateChoroplethMapProps {
  /** Map from state abbrev → numeric value to color by */
  data: Record<string, number>;
  selectedState: string | null;
  onStateClick: (state: string) => void;
  /** Label for the tooltip value, e.g. "Median Markup" */
  valueLabel?: string;
  /** Format function for tooltip value */
  formatValue?: (v: number) => string;
  /** Value the diverging scale centres on — the "average" in the Below/Above Average legend.
   *  Defaults to the median state, so blue/red always split the states (raw levels such as
   *  markups or US-vs-country ratios would otherwise sit all on one side of 0). Pass the
   *  national figure when there is one, e.g. the national median markup. */
  center?: number;
}

/** Diverging color scale: green → gray → red */
function getColor(
  value: number,
  min: number,
  max: number,
  isAhp: boolean,
  center = 0,
): string {
  // Normalize to -1..1 around the centre (the national figure), so blue/red really mean
  // below/above average — centring raw markups on 0 painted every state but one red.
  let t: number;
  const v = value - center, lo = min - center, hi = max - center;
  if (v <= 0) {
    t = lo < 0 ? v / Math.abs(lo) : 0; // below: -1..0
  } else {
    t = hi > 0 ? v / hi : 0; // above: 0..1
  }
  t = Math.max(-1, Math.min(1, t));

  if (isAhp) {
    // Navy blue (good) → white (neutral) → red (bad)
    if (t <= 0) {
      const a = Math.abs(t);
      const r = Math.round(0 + (250 - 0) * (1 - a));
      const g = Math.round(40 + (249 - 40) * (1 - a));
      const b = Math.round(104 + (246 - 104) * (1 - a));
      return `rgb(${r},${g},${b})`;
    } else {
      const r = Math.round(250 + (178 - 250) * t);
      const g = Math.round(249 + (34 - 249) * t);
      const b = Math.round(246 + (52 - 246) * t);
      return `rgb(${r},${g},${b})`;
    }
  } else {
    // Green (good) → gray (neutral) → red (bad)
    if (t <= 0) {
      const a = Math.abs(t);
      const r = Math.round(80 + (34 - 80) * a);
      const g = Math.round(80 + (197 - 80) * a);
      const b = Math.round(80 + (94 - 80) * a);
      return `rgb(${r},${g},${b})`;
    } else {
      const r = Math.round(80 + (239 - 80) * t);
      const g = Math.round(80 + (68 - 80) * t);
      const b = Math.round(80 + (68 - 80) * t);
      return `rgb(${r},${g},${b})`;
    }
  }
}

export function StateChoroplethMap({
  data,
  selectedState,
  onStateClick,
  valueLabel = 'Median Markup',
  formatValue = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`,
  center: centerProp,
}: StateChoroplethMapProps) {
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.design === 'civic';

  const { min, max } = useMemo(() => {
    const vals = Object.values(data);
    if (vals.length === 0) return { min: -50, max: 50 };
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [data]);
  const center = useMemo(() => {
    if (centerProp != null) return centerProp;
    const vals = Object.values(data).filter(Number.isFinite).sort((a, b) => a - b);
    return vals.length ? vals[Math.floor(vals.length / 2)] : 0;
  }, [data, centerProp]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  }, []);

  const hoveredValue = hoveredState && data[hoveredState] != null ? data[hoveredState] : null;
  const noDataColor = isAhp
    ? (isDark ? 'var(--civic-navy-deep)' : '#e5e5e5')
    : (isDark ? '#222' : '#333');

  return (
    <div className="relative w-full" onMouseMove={handleMouseMove}>
      <ComposableMap
        projection="geoAlbersUsa"
        className="w-full h-auto"
        style={{ maxHeight: '420px' }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const stateName = geo.properties.name;
              const stateCode = stateAbbreviations[stateName];
              if (!stateCode) return null;

              const value = data[stateCode];
              const hasData = value != null;
              const isSelected = stateCode === selectedState;
              const isHovered = stateCode === hoveredState;

              let fill: string;
              if (hasData) {
                fill = getColor(value, min, max, isAhp, center);
              } else {
                fill = noDataColor;
              }

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => setHoveredState(stateCode)}
                  onMouseLeave={() => setHoveredState(null)}
                  onClick={() => onStateClick(stateCode)}
                  style={{
                    default: {
                      fill,
                      stroke: isSelected
                        ? (isAhp ? 'var(--civic-gold)' : '#00ff41')
                        : (isAhp ? (isDark ? 'rgba(var(--civic-cream-rgb),0.2)' : 'rgba(0,0,0,0.15)') : 'rgba(255,255,255,0.15)'),
                      strokeWidth: isSelected ? 2.5 : 0.5,
                      outline: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: hasData ? 1 : 0.4,
                    },
                    hover: {
                      fill,
                      stroke: isAhp ? 'var(--civic-gold)' : '#00ff41',
                      strokeWidth: 2,
                      outline: 'none',
                      cursor: 'pointer',
                      opacity: 1,
                      filter: 'brightness(1.15)',
                    },
                    pressed: {
                      fill,
                      stroke: isAhp ? 'var(--civic-gold)' : '#00ff41',
                      strokeWidth: 2.5,
                      outline: 'none',
                      cursor: 'pointer',
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {/* Tooltip */}
      {hoveredState && (
        <div
          className="fixed z-50 pointer-events-none px-3 py-2 rounded-lg shadow-lg text-sm"
          style={{
            left: tooltipPos.x + 12,
            top: tooltipPos.y - 40,
            backgroundColor: isAhp ? (isDark ? 'var(--civic-card-dark)' : '#fff') : '#111',
            border: `1px solid ${isAhp ? (isDark ? 'rgba(var(--civic-gold-rgb),0.3)' : '#e5e7eb') : 'rgba(255,255,255,0.15)'}`,
            color: isAhp ? (isDark ? 'var(--civic-cream)' : 'var(--civic-ink)') : '#fff',
          }}
        >
          <div className="font-semibold">{hoveredState}</div>
          {hoveredValue != null ? (
            <div className="opacity-80">
              {valueLabel}: {formatValue(hoveredValue)}
            </div>
          ) : (
            <div className="opacity-50">No data</div>
          )}
        </div>
      )}

      {/* Color legend */}
      <div className="flex items-center justify-center gap-2 mt-2 text-xs">
        <span style={{ color: isAhp ? (isDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)') : '#22c55e' }}>
          Below Average
        </span>
        <div
          className="h-3 rounded"
          style={{
            width: '120px',
            background: isAhp
              ? 'linear-gradient(to right, var(--civic-navy), var(--civic-cream), var(--civic-red))'
              : 'linear-gradient(to right, #22c55e, #505050, #ef4444)',
          }}
        />
        <span style={{ color: isAhp ? (isDark ? 'var(--civic-red-soft)' : 'var(--civic-red)') : '#ef4444' }}>
          Above Average
        </span>
      </div>
    </div>
  );
}
